import { memo, useMemo } from "react";
import Snackbar from "@mui/material/Snackbar";
import type { SnackbarProps } from "@mui/material/Snackbar";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import StringHelpers from "helpers/StringHelpers";
import SnackAlert from "./components/SnackAlert";
import useMergeProps from "hooks/useMergeProps";

const DEFAULT_AUTO_HIDE_DURATION = 6000;

const DEFAULT_PROPS = {
  autoHideDuration: DEFAULT_AUTO_HIDE_DURATION,
};

interface SnackBarProps {
  message: string;
  description?: string;
  type: "info" | "success" | "warning" | "error";
  onClose: () => void;
  action?: React.ReactNode;
  autoHideDuration?: SnackbarProps["autoHideDuration"];
}

function SnackBar(props: SnackBarProps) {
  const { message, description, type, onClose, autoHideDuration, action } = useMergeProps(DEFAULT_PROPS, props);

  const slugifiedMessage = useMemo(() => StringHelpers.slugify(message || "info"), [message]);

  return (
    <ThemeProvider2024>
      <Snackbar
        key={slugifiedMessage}
        open={!!message}
        autoHideDuration={autoHideDuration}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        aria-live="polite"
      >
        <SnackAlert elevation={2} onClose={onClose} severity={type} action={action} description={description}>
          {message}
        </SnackAlert>
      </Snackbar>
    </ThemeProvider2024>
  );
}

export default memo(SnackBar);
